/* Sprint 2R-2B — Admin Live Routes Overview.
 *
 * Read-only overview of all currently active b2b_routes with driver location +
 * stop counts. Admin can see full driver info here (it's the admin overview),
 * but NO customer revenue / Stripe / Billy / EMA margin is exposed.
 */
import React from "react";
import { adminApi } from "../../lib/api";
import { Loader2, RefreshCw, Truck, AlertCircle, MapPin } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const driverMarker = (stale) =>
  new L.DivIcon({
    className: "ema-admin-driver-marker",
    html: `<div style="background:${stale ? "#94a3b8" : "#dc2626"};width:30px;height:30px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;">🚚</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

export default function AdminLiveRoutesPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  const load = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await adminApi().get("/admin/live/routes");
      setData(r.data);
    } catch (e) {
      // No-op
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s
  React.useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser live rute-oversigt...</div>;

  const items = data?.items || [];
  const withGps = items.filter((it) => it.last_location && !it.last_location.stale);
  const stale = items.filter((it) => it.last_location && it.last_location.stale);
  const noLoc = items.filter((it) => !it.last_location);

  const allPoints = items
    .filter((it) => it.last_location?.lat != null)
    .map((it) => [it.last_location.lat, it.last_location.lng]);
  const center = allPoints.length > 0 ? allPoints[0] : [55.6761, 12.5683];

  return (
    <div className="space-y-6" data-testid="admin-live-routes-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-xl text-brand-dark">Live Rute-oversigt</h2>
          <p className="text-sm text-brand-text2 mt-1">
            {items.length} aktive ruter · {withGps.length} med frisk GPS · {stale.length} GPS-stale · {noLoc.length} uden position
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="px-3 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="admin-live-routes-refresh"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""}/> Opdater
        </button>
      </div>

      <div className="bg-white border border-brand" style={{ height: 460 }}>
        <MapContainer center={center} zoom={9} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
          {items
            .filter((it) => it.last_location?.lat != null)
            .map((it) => (
              <Marker
                key={it.route_id}
                position={[it.last_location.lat, it.last_location.lng]}
                icon={driverMarker(it.last_location.stale)}
              >
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold mb-1">{it.route_number || it.route_id.slice(0, 8)}</div>
                    <div className="text-xs text-gray-600">Chauffør: {it.driver_name || "—"}</div>
                    {it.driver_phone && <div className="text-xs text-gray-600">Tlf: {it.driver_phone}</div>}
                    <div className="text-xs mt-1">
                      {it.parcels_delivered}/{it.parcels_total} leveret · {it.parcels_remaining} tilbage
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      GPS for {it.last_location.gps_age_seconds}s siden
                      {it.last_location.stale && <span className="text-amber-600"> (stale)</span>}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
        </MapContainer>
      </div>

      {/* Table */}
      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-live-routes-table">
          <thead>
            <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
              <th className="p-3">Rute</th>
              <th className="p-3">Chauffør</th>
              <th className="p-3">Status</th>
              <th className="p-3">Lifecycle</th>
              <th className="p-3">Pakker</th>
              <th className="p-3">GPS</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-brand-text2">Ingen aktive ruter</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.route_id} className="border-b border-gray-100" data-testid={`admin-live-route-${it.route_id}`}>
                <td className="p-3 font-mono text-xs">{it.route_number || it.route_id.slice(0, 8)}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Truck size={12}/> {it.driver_name || <span className="text-brand-text2">Ingen tildelt</span>}
                  </div>
                  {it.driver_phone && <div className="text-xs text-brand-text2">{it.driver_phone}</div>}
                </td>
                <td className="p-3 text-xs">{it.status || "—"}</td>
                <td className="p-3 text-xs">{it.lifecycle_status || "—"}</td>
                <td className="p-3 text-xs">{it.parcels_delivered}/{it.parcels_total}</td>
                <td className="p-3 text-xs">
                  {it.last_location ? (
                    <div className="flex items-center gap-1">
                      <MapPin size={12} className={it.last_location.stale ? "text-amber-600" : "text-emerald-600"}/>
                      {it.last_location.stale ? (
                        <span className="text-amber-700">Stale ({it.last_location.gps_age_seconds}s)</span>
                      ) : (
                        <span className="text-emerald-700">Live ({it.last_location.gps_age_seconds}s)</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-brand-text2 flex items-center gap-1"><AlertCircle size={12}/> Ingen position</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
